import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PostVideoQuestionsActivationResult,
  PostVideoQuestionsActivationOptions,
  GetVideoQuestionsActivationOptions,
  GetVideoQuestionsActivationResult,
  getVideoQuestionsActivation,
  PostVideoQuestionsActivationFields,
  GetVideoQuestionsActivationFields,
  postVideoQuestionsActivation,
} from '../fetchers/video/questions/activation';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetVideoQuestionsActivationOptions<Fields extends GetVideoQuestionsActivationFields> =
  GetVideoQuestionsActivationOptions<Fields>;

/**
 * Get the status of whether the user can ask video questions
 * This method returns the status of whether the authenticated user can ask questions about the specified video.
 */

export function useGetVideoQuestionsActivation<Fields extends GetVideoQuestionsActivationFields>(
  getOptions:
    | UseGetVideoQuestionsActivationOptions<Fields>
    | (() => UseGetVideoQuestionsActivationOptions<Fields> | null),
  config?: SWRConfiguration<GetVideoQuestionsActivationResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options
      ? `/videos/${options.where.videoId}/questions/activation${serializeQuery(options)}`
      : () => null,
    options
      ? () => {
          return getVideoQuestionsActivation({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoQuestionsActivation, {
    endpoint: '/videos/:videoId/questions/activation',
    method: 'GET',
  });
}

/**
 * Get the status of whether the user can ask video questions
 * This method returns the status of whether the authenticated user can ask questions about the specified video.
 */

export function useGetVideoQuestionsActivationLazy<
  Fields extends GetVideoQuestionsActivationFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetVideoQuestionsActivationResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetVideoQuestionsActivationOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/questions/activation${serializeQuery(options)}`,
          getVideoQuestionsActivation({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetVideoQuestionsActivationLazy, {
    endpoint: '/videos/:videoId/questions/activation',
    method: 'GET',
  });
}

type UsePostVideoQuestionsActivationOptions<Fields extends PostVideoQuestionsActivationFields> =
  PostVideoQuestionsActivationOptions<Fields>;

/**
 * Enable the user to ask questions about a video
 * This method enables the authenticated user to ask questions about the specified video.
 *
 * This method works only for videos with completed transcripts in English.
 * The authenticated user must have permission to edit the video.
 */

export function usePostVideoQuestionsActivation<
  Fields extends PostVideoQuestionsActivationFields,
>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<
    PostVideoQuestionsActivationResult<Fields> | undefined
  >();

  let call = useCallback(
    async (options: UsePostVideoQuestionsActivationOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/videos/${options.where.videoId}/questions/activation${serializeQuery(options)}`,
          postVideoQuestionsActivation({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePostVideoQuestionsActivation, {
    endpoint: '/videos/:videoId/questions/activation',
    method: 'POST',
  });
}
